import { lazy, Suspense, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { reportQueryError } from './lib/queryErrorReporting';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { TooltipProvider } from './components/ui/tooltip';
import Index from './pages/Index';
import EventRequestsWarmup from './pages/client/EventRequestsWarmup';
import { EventLoader } from './components/auth/EventLoader';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PublicRoute } from './components/auth/PublicRoute';
import { StagingBanner } from './components/ui/StagingBanner';
import { ImageIssueBanner } from './components/ui/ImageIssueBanner';
import './App.css';

// The landing page stays eager: it is the most-visited route and splitting it
// would add a chunk waterfall before first paint. Everything else is code-split
// per route so each visitor only downloads what they navigate to.
const Auth = lazy(() => import('./pages/Auth'));
const MagicLink = lazy(() => import('./pages/MagicLink').then((m) => ({ default: m.MagicLink })));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const EventRoute = lazy(() => import('./pages/client/EventRoute'));
import { ExploraLoader } from './pages/explora/ExploraLoader';

const ExploraLayout = lazy(() => import('./pages/explora/ExploraLayout'));
const GaleriaSection = lazy(() => import('./pages/explora/GaleriaSection'));
const ResenasSection = lazy(() => import('./pages/explora/ResenasSection'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const TemplatePreview = lazy(() => import('./pages/TemplatePreview'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const PublishedInvitation = lazy(() => import('./pages/PublishedInvitation'));
// DEV-only design preview of the sign-in flow; the import is dropped from production builds
const AuthPreview = import.meta.env.DEV ? lazy(() => import('./pages/dev/AuthPreview')) : null;

// Admin pages
const AdminLayout = lazy(() => import('./components/layout/AdminLayout').then((m) => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const LeadsManagement = lazy(() => import('./pages/Admin/LeadsManagement').then((m) => ({ default: m.LeadsManagement })));
const EventsManagement = lazy(() => import('./pages/Admin/EventsManagement'));
const TemplatesManagement = lazy(() => import('./pages/Admin/TemplatesManagement').then((m) => ({ default: m.TemplatesManagement })));
const LocationsManagement = lazy(() => import('./pages/Admin/LocationsManagement').then((m) => ({ default: m.LocationsManagement })));
const MenuManagement = lazy(() => import('./pages/Admin/MenuManagement').then((m) => ({ default: m.MenuManagement })));
const ServicesManagement = lazy(() => import('./pages/Admin/ServicesManagement').then((m) => ({ default: m.ServicesManagement })));
const FaqsManagement = lazy(() => import('./pages/Admin/FaqsManagement').then((m) => ({ default: m.FaqsManagement })));
const Settings = lazy(() => import('./pages/Admin/Settings').then((m) => ({ default: m.Settings })));
const MaintenanceManagement = lazy(() => import('./pages/Admin/MaintenanceManagement').then((m) => ({ default: m.MaintenanceManagement })));
const PricingManagement = lazy(() => import('./pages/Admin/PricingManagement').then((m) => ({ default: m.PricingManagement })));
const LaCasaDigitalManagement = lazy(() => import('./pages/Admin/LaCasaDigitalManagement').then((m) => ({ default: m.LaCasaDigitalManagement })));
const ContractTemplateManagement = lazy(() => import('./pages/Admin/ContractTemplateManagement').then((m) => ({ default: m.ContractTemplateManagement })));
const DocumentTemplateManagement = lazy(() => import('./pages/Admin/DocumentTemplateManagement').then((m) => ({ default: m.DocumentTemplateManagement })));

/** Neutral full-screen fallback while a route chunk loads (no spinner flash). */
function RouteFallback() {
  return (
    <div role="status" className="min-h-screen bg-background">
      <span className="sr-only">Cargando...</span>
    </div>
  );
}

/** Fallback for admin sub-routes: keeps the AdminLayout mounted. */
function AdminContentFallback() {
  return (
    <div role="status" className="min-h-[50vh]">
      <span className="sr-only">Cargando...</span>
    </div>
  );
}

/** Wraps an admin page so the layout stays mounted while its chunk loads. */
function AdminPage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<AdminContentFallback />}>{children}</Suspense>;
}

const MAINTENANCE_MODE = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

// Create a client. Global cache-level onError reports every query/mutation
// failure to Sentry (5xx / network / unknown only); per-call onError toasts are
// untouched and still fire alongside this.
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => reportQueryError(error, query.queryKey, 'query'),
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) =>
      reportQueryError(error, mutation.options.mutationKey, 'mutation'),
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

function App() {
  if (MAINTENANCE_MODE) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Maintenance />
      </Suspense>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <StagingBanner />
        <ImageIssueBanner />
        <Toaster />
        <ExploraLoader />
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <PublicRoute>
                  <Index />
                </PublicRoute>
              }
            />
            <Route
              path="/auth"
              element={
                <PublicRoute>
                  <Auth />
                </PublicRoute>
              }
            />
            <Route
              path="/auth/magic-link"
              element={<MagicLink />}
            />
            {AuthPreview ? <Route path="/dev/auth-preview" element={<AuthPreview />} /> : null}
            {/* Explora: public and open to signed-in users too (no PublicRoute), one shell for its three sections */}
            <Route element={<ExploraLayout />}>
              {/* the map is rendered by the layout (kept mounted across sections); the route only matches the path */}
              <Route path="/locaciones" element={null} />
              <Route path="/galeria" element={<GaleriaSection />} />
              <Route path="/resenas" element={<ResenasSection />} />
            </Route>

            {/* Legal Pages - Public */}
            <Route path="/privacidad" element={<PrivacyPolicy />} />

            {/* Client Routes - Protected */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/:id"
              element={
                <EventRequestsWarmup>
                  {/* One skeleton for the whole load: the auth check, the lazy EventRoute chunk (a boundary of its own, so the
                      app-level blank fallback never shows between them) and EventRoute's own loading all render EventLoader */}
                  <ProtectedRoute loader={<EventLoader />}>
                    <Suspense fallback={<EventLoader />}>
                      <EventRoute />
                    </Suspense>
                  </ProtectedRoute>
                </EventRequestsWarmup>
              }
            />
            <Route
              path="/preview-template/:templateId"
              element={
                <ProtectedRoute>
                  <TemplatePreview />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes - Protected with Layout */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminPage><AdminDashboard /></AdminPage>} />
              <Route path="dashboard" element={<AdminPage><AdminDashboard /></AdminPage>} />
              <Route path="leads" element={<AdminPage><LeadsManagement /></AdminPage>} />
              <Route path="events" element={<AdminPage><EventsManagement /></AdminPage>} />
              <Route
                path="templates"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <AdminPage><TemplatesManagement /></AdminPage>
                  </ProtectedRoute>
                }
              />
              <Route path="locations" element={<AdminPage><LocationsManagement /></AdminPage>} />
              <Route path="menu" element={<AdminPage><MenuManagement /></AdminPage>} />
              <Route path="services" element={<AdminPage><ServicesManagement /></AdminPage>} />
              <Route path="faqs" element={<AdminPage><FaqsManagement /></AdminPage>} />
              <Route path="maintenance" element={<AdminPage><MaintenanceManagement /></AdminPage>} />
              <Route
                path="pricing"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <AdminPage><PricingManagement /></AdminPage>
                  </ProtectedRoute>
                }
              />
              <Route
                path="la-casa-digital"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <AdminPage><LaCasaDigitalManagement /></AdminPage>
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <AdminPage><Settings /></AdminPage>
                  </ProtectedRoute>
                }
              />
              <Route
                path="contract-template"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <AdminPage><ContractTemplateManagement /></AdminPage>
                  </ProtectedRoute>
                }
              />
              <Route
                path="document-templates"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <AdminPage><DocumentTemplateManagement /></AdminPage>
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Public Invitation Routes */}
            <Route path="/inv/:slug" element={<PublishedInvitation />} />
            <Route path="/inv/:slug/:token" element={<PublishedInvitation />} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
